import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { avatarUrl } from "../lib/avatar";
import { Trophy, Zap } from "lucide-react";

export default function Leaderboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const [topRes, comebackRes] = await Promise.all([
        supabase.from("profiles").select("*").neq("role", "admin")
          .order("engagement_score", { ascending: false }).limit(30),
        supabase.from("profiles").select("*").neq("role", "admin")
          .order("comeback_score", { ascending: false }).limit(5),
      ]);
      setData({ top_players: topRes.data || [], comeback: comebackRes.data || [] });
    })();
  }, []);

  if (!data) return <div className="p-10 font-hand text-3xl">loading the rankings…</div>;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-10">
      <header>
        <div className="font-hand text-marker-red text-3xl rotate-[-1deg]">— ranked by helping —</div>
        <h1 className="font-display font-extrabold text-5xl sm:text-6xl uppercase tracking-tight">
          The <span className="highlight-marker">Leaderboard</span>
        </h1>
        <p className="font-mono text-xs uppercase tracking-widest text-ink/60 mt-2">
          Score = likes + comments + shares your strategies have generated.
        </p>
      </header>

      <section>
        <h2 className="font-display font-extrabold text-2xl uppercase tracking-tight mb-4 flex items-center gap-2">
          <Zap className="text-marker-red" /> Comeback movers — this month
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.comeback.map((p) => (
            <Link key={p.id} to={`/u/${p.id}`} className="brutal-card p-4 flex items-center gap-3" data-testid={`comeback-${p.id}`}>
              <img src={avatarUrl(p.avatar_url, p.id)} alt={p.name} className="w-12 h-12 border-2 border-ink blob bg-cream" />
              <div className="min-w-0 flex-1">
                <div className="font-display font-extrabold uppercase tracking-tight truncate">{p.name}</div>
                <div className="font-mono text-xs text-ink/60 truncate">{p.participant_id}</div>
              </div>
              <span className="tag-pill" style={{ background: "#FF3333", color: "#fff" }}>+{p.comeback_score}</span>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-extrabold text-2xl uppercase tracking-tight mb-4 flex items-center gap-2">
          <Trophy className="text-marker-red" /> Top players
        </h2>
        <ol className="space-y-3" data-testid="leaderboard-list">
          {data.top_players.map((p, i) => (
            <li key={p.id} className="brutal-card p-4 flex items-center gap-4" data-testid={`leaderboard-row-${p.id}`}>
              <div className={`font-display font-extrabold text-3xl w-12 shrink-0 ${i < 3 ? "text-marker-red" : "text-ink/70"}`}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <img src={avatarUrl(p.avatar_url, p.id)} alt={p.name} className="w-14 h-14 border-2 border-ink blob bg-cream" />
              <Link to={`/u/${p.id}`} className="flex-1 min-w-0">
                <div className="font-display font-extrabold text-xl uppercase tracking-tight truncate">{p.name}</div>
                <div className="font-mono text-xs text-ink/60 truncate">
                  {p.location || "—"} · target ${Number(p.goal_amount || 0).toLocaleString()}
                </div>
              </Link>
              <div className="text-right">
                <div className="font-display font-extrabold text-2xl">{p.engagement_score}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest">engagement</div>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
